import { CalendarClockIcon, CalendarPlusIcon, TriangleAlertIcon, ZapIcon } from 'lucide-react';

import type { ManagedCustomDashboardTab } from '@/lib/api/custom-dashboard';
import { cn } from '@/lib/utils';
import { type DataUpdateBlocker, dataUpdateBlocker } from './dataUpdateAvailability';
import { StatusNote } from './TabStatusParts';
import { formatWeekdays, formatWeekdaysShort } from './updateSettingsFormat';

/** 定期更新が失敗になる理由（開始条件はデータ更新と同じ。実行中は次の回に影響しないため持たない）。 */
const FAILING_HINTS: Record<Exclude<DataUpdateBlocker, 'running'>, string> = {
  'no-active-revision': '未公開のため失敗',
  'no-details': 'details.txt なしで失敗',
};

type ChipTone = 'set' | 'unset' | 'failing';

const CHIP_TONE: Record<ChipTone, string> = {
  set: 'border-primary/30 bg-primary/10 font-semibold text-foreground hover:border-primary/50 hover:bg-primary/15 [&_svg]:text-primary',
  unset:
    'border-dashed border-muted-foreground/40 font-medium text-muted-foreground hover:border-solid hover:border-primary/50 hover:text-foreground',
  failing: 'border-warning/40 bg-warning-muted font-semibold text-warning hover:border-warning/70',
};

export interface TabUpdateSettingsStatusProps {
  tab: ManagedCustomDashboardTab;
  /** 定期更新の設定ダイアログを開く。 */
  onRequest: () => void;
}

/**
 * タブ行の「定期更新」欄。設定の有無を見比べられるよう、設定ありは塗り、未設定は点線のチップで出し、
 * チップ自体を設定ダイアログの入口にする。設定していても定期実行が失敗するタブは注意の色で知らせる。
 */
export function TabUpdateSettingsStatus({ tab, onRequest }: TabUpdateSettingsStatusProps) {
  const settings = tab.updateSettings;
  const blocker = dataUpdateBlocker(tab);
  const failingHint =
    settings === null || blocker === null || blocker === 'running' ? null : FAILING_HINTS[blocker];
  const tone: ChipTone = settings === null ? 'unset' : failingHint !== null ? 'failing' : 'set';
  const label =
    settings === null
      ? `${tab.name}の定期更新を設定`
      : `${tab.name}の定期更新を変更（${formatWeekdays(settings.weekdays)}）`;
  const Icon =
    tone === 'unset'
      ? CalendarPlusIcon
      : tone === 'failing'
        ? TriangleAlertIcon
        : CalendarClockIcon;

  return (
    <div>
      <button
        type="button"
        aria-label={label}
        title={settings === null ? undefined : formatWeekdays(settings.weekdays)}
        onClick={onRequest}
        className={cn(
          'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:size-3.5 [&_svg]:shrink-0',
          CHIP_TONE[tone],
        )}
      >
        <Icon aria-hidden />
        {settings === null ? '未設定' : formatWeekdaysShort(settings.weekdays)}
      </button>
      {failingHint !== null ? (
        <StatusNote tone="warning">{failingHint}</StatusNote>
      ) : (
        settings?.autoActivate && (
          <StatusNote>
            <ZapIcon aria-hidden />
            完了後に自動公開
          </StatusNote>
        )
      )}
    </div>
  );
}
